import React from 'react';
import { CartContext, cartTotals, discountableSubtotal } from './CartContext';
import { loadCart, saveCart } from './cartStorage';
import { CartState, DealCartLine, MenuItem, DrinkSelection, normalizeItemNotes } from './cartTypes';
import { validateDiscountCode } from '../../services/dashboardService';

function newLineId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `line-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<CartState>(() => loadCart());

  React.useEffect(() => {
    saveCart(state);
  }, [state]);

  const { count, subtotal } = React.useMemo(() => cartTotals(state.lines), [state.lines]);
  const promoSubtotal = React.useMemo(() => discountableSubtotal(state.lines), [state.lines]);
  const discount = Math.max(0, Math.min(promoSubtotal, Number(state.promoDiscountAmount || 0)));
  const total = Math.max(0, subtotal - discount);

  React.useEffect(() => {
    let cancelled = false;
    async function refreshDiscount() {
      if (!state.promoCode) {
        if (state.promoDiscountAmount) {
          setState((prev) => ({ ...prev, promoDiscountAmount: undefined }));
        }
        return;
      }
      if (promoSubtotal <= 0) {
        if (state.promoDiscountAmount) {
          setState((prev) => ({ ...prev, promoDiscountAmount: 0 }));
        }
        return;
      }
      try {
        const result = await validateDiscountCode(state.promoCode, promoSubtotal);
        if (cancelled) return;
        setState((prev) => {
          if (prev.promoCode !== state.promoCode) return prev;
          if ((prev.promoDiscountAmount || 0) === result.discountAmount) return prev;
          return { ...prev, promoDiscountAmount: result.discountAmount };
        });
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : '';
        const invalidPromo =
          message.includes('Promo code not found') ||
          message.includes('not active') ||
          message.includes('expired') ||
          message.includes('Enter a promo');
        if (invalidPromo) {
          setState((prev) => ({ ...prev, promoCode: undefined, promoDiscountAmount: undefined }));
        }
      }
    }
    void refreshDiscount();
    return () => {
      cancelled = true;
    };
    // promoDiscountAmount intentionally omitted — re-running when it changes caused redundant re-validation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.lines, state.promoCode, promoSubtotal]);

  const api = React.useMemo(
    () => ({
      state,
      count,
      subtotal,
      discount,
      total,
      addItem: (
        item: MenuItem,
        quantity = 1,
        drinkSelection?: DrinkSelection,
        notes?: string,
        meal?: { isMeal: boolean; mealUpgradePrice: number },
        saladSaucePreference?: { value: string; label: string },
        flavourChoice?: { value: string; label: string }
      ) => {
        if (item.isSoldOut) return;
        if (item.includesDrinkChoice && !drinkSelection) return;
        if (item.saladSaucePreferenceListId && !saladSaucePreference) return;
        if (item.flavourChoiceListId && !flavourChoice) return;
        const normalizedNotes = normalizeItemNotes(notes);
        const isMeal = Boolean(meal?.isMeal && item.offersMealUpgrade);
        const mealUpgradePrice = isMeal ? Math.max(0, Number(meal?.mealUpgradePrice) || 0) : undefined;
        const saladSauce = item.saladSaucePreferenceListId ? saladSaucePreference : undefined;
        setState((prev) => {
          const existing = prev.lines.find(
            (l) =>
              l.kind === 'item' &&
              l.item.id === item.id &&
              (l.drinkSelection?.menuItemId ?? '') === (drinkSelection?.menuItemId ?? '') &&
              (normalizeItemNotes(l.notes) ?? '') === (normalizedNotes ?? '') &&
              Boolean(l.isMeal) === isMeal &&
              (l.saladSaucePreference?.value ?? '') === (saladSauce?.value ?? '') &&
              (l.flavourChoice?.value ?? '') === (flavourChoice?.value ?? '')
          );
          if (existing && existing.kind === 'item') {
            return {
              ...prev,
              lines: prev.lines.map((l) =>
                l.lineId === existing.lineId
                  ? { ...l, quantity: Math.max(1, l.quantity + quantity) }
                  : l
              ),
            };
          }
          return {
            ...prev,
            lines: [
              ...prev.lines,
              {
                kind: 'item',
                lineId: newLineId(),
                item,
                quantity: Math.max(1, quantity),
                drinkSelection,
                notes: normalizedNotes,
                isMeal: isMeal || undefined,
                mealUpgradePrice,
                saladSaucePreference: saladSauce,
                flavourChoice: item.flavourChoiceListId ? flavourChoice : undefined,
              },
            ],
          };
        });
      },
      addDeal: (payload: Omit<DealCartLine, 'kind' | 'lineId'>) => {
        const lineId = newLineId();
        setState((prev) => ({
          ...prev,
          lines: [...prev.lines, { kind: 'deal', lineId, ...payload }],
        }));
      },
      removeLine: (lineId: string) => {
        setState((prev) => ({ ...prev, lines: prev.lines.filter((l) => l.lineId !== lineId) }));
      },
      removeItem: (lineId: string) => {
        setState((prev) => ({ ...prev, lines: prev.lines.filter((l) => l.lineId !== lineId) }));
      },
      setQuantity: (lineId: string, quantity: number) => {
        setState((prev) => ({
          ...prev,
          lines: prev.lines
            .map((l) => (l.lineId === lineId ? { ...l, quantity: Math.max(1, quantity) } : l))
            .filter((l) => l.quantity > 0),
        }));
      },
      setLineNotes: (lineId: string, notes: string) => {
        setState((prev) => ({
          ...prev,
          lines: prev.lines.map((l) =>
            l.lineId === lineId && l.kind === 'item' ? { ...l, notes: notes || undefined } : l
          ),
        }));
      },
      clear: () => setState({ lines: [] }),
      applyPromoCode: async (code: string) => {
        try {
          if (promoSubtotal <= 0) {
            return {
              error: 'No items in your cart are eligible for this promo code.',
            };
          }
          const result = await validateDiscountCode(code, promoSubtotal);
          if (result.discountAmount <= 0) {
            return { error: 'This promo code does not reduce your order total.' };
          }
          setState((prev) => ({
            ...prev,
            promoCode: result.code,
            promoDiscountAmount: result.discountAmount,
          }));
          return { code: result.code, discountAmount: result.discountAmount };
        } catch (e) {
          setState((prev) => ({ ...prev, promoCode: undefined, promoDiscountAmount: undefined }));
          return { error: e instanceof Error ? e.message : 'Invalid promo code' };
        }
      },
      clearPromoCode: () => setState((prev) => ({ ...prev, promoCode: undefined, promoDiscountAmount: undefined })),
    }),
    [count, state, subtotal, promoSubtotal, discount, total]
  );

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}

